import { Document, Image, Page, PDFDownloadLink, StyleSheet, Text, View } from "@react-pdf/renderer";
import { CircularProgress, IconButton } from "@material-ui/core";
import { Download } from "react-feather";
import React, { FC, useMemo, useState } from "react";
import { makeStyles } from "@material-ui/core/styles";
import {getCurrectLanguageFullName, getTextByCurrentLanguage} from "../../../../../../utils/functions";
import { UnitContainer } from "./UnitContainer";
import { ClpData } from "../../../../../../models/Clp";
import { useSelector } from "../../../../../../redux/store";
import { IUser } from "../../../../../../models/User";
import { getUserProfile } from "../../../../../../selectors";
import { Property } from "../../../../../../models/Property";
import { useTranslation } from "react-i18next";

const useStyles = makeStyles({
  downloadBox: {
    display: "flex",
    marginLeft: "auto",
    width: '50px'
  },
})

const styles = StyleSheet.create({
  page: {
    flexDirection: "row",
    backgroundColor: "#E4E4E4",
    padding: 10,
  },

  disclaimerRoot: {
    // width: "100%",
    height: "42px",
    borderRadius: "3px",
    backgroundColor: "#0B235C",
    color: "rgba(0, 255, 240, 1)",
    display: "flex",
    flexDirection: 'row',
    justifyContent: "center",
    alignItems: "center",
  },

  disclaimerImage: {
    width: "24px",
    height: "24px",
    right: 8,
  },

  disclaimerText: {
    fontWeight: 600,
    fontSize: "9px",
    fontStyle: "Manrope",
  },

  // disclaimer: {

  // },

  header: {
    width: "100%",
    // color: "white",
    fontWeight: 800,
    fontSize: "24px",
    fontStyle: "Manrope",
    marginTop: "20px",
    marginBottom: "16px",
  },

  tablePage: {
    padding: 10,
  },

  section: {
    margin: 10,
    padding: 10,
    flexGrow: 1,
  },
  bulletPoint: {
    width: 10,
    fontSize: 10,
  },
  item: {
    flexDirection: "row",
    marginBottom: 5,
  },
  itemContent: {
    flex: 1,
    fontSize: 10,
  },
  coverWrapper: {
    width: "100%",
    height: "100%",
    padding: 30,
  },
  coverTitle: {
    fontSize: 54,
    fontFamily: "Manrope",
    color: "#00FFF0",
    fontWeight: "bold",
    width: "70%",
  },
  coverSubtitle: {
    marginTop: 36,
    fontSize: 18,
    fontFamily: "Manrope",
    color: "#00FFF0",
    width: "70%",
  },
  infoIcon: {
    backgroundColor: 'rgba(0, 255, 240, 1)',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    height: '18px',
    width: '18px',
    borderRadius: '500px',
    marginRight: '7px'
  },
});

type PropsType = {
  clpData: ClpData[],
  property: Property
}

const DownloadDocument: FC<PropsType> = ({clpData, property}) => {
  const classes = useStyles()
  const {t, i18n} = useTranslation()
  const userProfile = useSelector<IUser | undefined>(getUserProfile);
  const company = useSelector((state) => state.companyReducer.company);
  const [newDocument, setNewDocument] = useState<any>(<Document/>)
  const [isPrepareDocument, setIsPrepareDocument] = useState(false)

  const clpDataMap: [string, ClpData[]][] | null = useMemo(() => {
    if (clpData) {
      const map = new Map<string, ClpData[]>();
      clpData.forEach((entry) => {
        const data = map.get(entry.title);
        if (data) {
          map.set(`${entry.title} - ${entry.name}`, [...data, entry]);
        } else {
          map.set(`${entry.title} - ${entry.name}`, [entry]);
        }
      });
      return Array.from(map.entries());
    }
    return null;
  }, [clpData]);

  const filterCompletedBlock = (data: ClpData[]) => {
    if (data) {
      return data.filter((entry) => (
        !entry.clps.every((clp) => clp.is_done)
      ))
    }
    return data
  }

  const getPositionForPage = (data:  ClpData[] | undefined) => {
    let counterForCalculate = 0
    let newPageIndexes: Array<{from: number, to: number}> = []
    const validElementValue = 12
    if (data) {
      data.forEach((clp, index) => {
        counterForCalculate += clp.clps.length + 1
        if (counterForCalculate > validElementValue) {
          newPageIndexes.push({
            from: newPageIndexes.length > 0 ?
              newPageIndexes[newPageIndexes.length - 1].to : 0,
            to: index + 1
          })
          counterForCalculate = 0
        }

        if (index === data.length - 1) {
          if (newPageIndexes.length === 0) {
            newPageIndexes.push({
              from: newPageIndexes.length > 0 ?
                newPageIndexes[newPageIndexes.length - 1].to + 1 : 0,
              to: index + 1
            })
          } else if (index + 1 !== newPageIndexes[newPageIndexes.length - 1].to) {
            newPageIndexes.push({
              from: newPageIndexes.length > 0 ?
                newPageIndexes[newPageIndexes.length - 1].to : 0,
              to: index + 1
            })
          }
        }
      })
    }

    return newPageIndexes
  }

  const DOC = () => {
    if (!property && clpData) {
      return () => (
        <Document>
          <Page size="A4" style={styles.page}>
            <View
              style={{
                width: "100%",
              }}
            >
              <Text>{t("CLP_PleaseSelectProperty")}</Text>
            </View>
          </Page>
        </Document>
      );
    }
    if (clpData) {
      return () => (
        <Document>
          <Page size="A4">
            <View style={styles.coverWrapper}>
              <Image
                style={{
                  width: "50px",
                  height: "50px",
                  marginTop: "80px",
                }}
                src={`${process.env.PUBLIC_URL}/static/img/icons/clp-cover-icon-2.0.png`}
              />
              <Text style={styles.coverTitle}>
                {t("CLP_AccessibilityChecklist")}
              </Text>
              <Text style={styles.coverSubtitle}>{getTextByCurrentLanguage(property.name)}</Text>
              <View
                style={{
                  position: "absolute",
                  bottom: styles.coverWrapper.padding,
                  left: styles.coverWrapper.padding,
                  width: "100%",
                }}
              >
                <View
                  style={{
                    position: "absolute",
                    bottom: 0,
                    left: 0,
                  }}
                >
                  <Image
                    style={{
                      width: "auto",
                      height: "45px",
                      objectFit: "contain",
                    }}
                    src={`${process.env.PUBLIC_URL}/static/img/icons/HD-accessibility-logo-light-2.0.png`}
                  />
                </View>
                <View
                  style={{
                    position: "absolute",
                    bottom: 0,
                    right: 0,
                    fontSize: 12,
                    color: "white",
                    fontFamily: "Manrope",
                  }}
                >
                  <Text>{getCurrectLanguageFullName()}</Text>
                  <Text>
                    {t("CLP_GeneratedOn")}{" "}
                    {new Date()
                      .toLocaleDateString(i18n.language, {
                        year: "numeric",
                        month: "long",
                        day: "2-digit",
                      })
                      .toLocaleUpperCase()}
                  </Text>
                  <Text>
                    {t("CLP_GeneratedBy")} {userProfile?.first_name}{" "}
                    {userProfile?.last_name}
                  </Text>
                </View>
              </View>
            </View>
            <Image
              style={{
                width: "100%",
                height: "100%",
                position: "absolute",
                zIndex: -1,
              }}
              src={`${process.env.PUBLIC_URL}/static/img/covers/bg_checklist.png`}
            />
          </Page>
          {getPositionForPage(filterCompletedBlock(clpData))
            .map((item, index) => {
              let filteredClpDataMap: any = []
              if (clpDataMap) {
                filteredClpDataMap = clpDataMap.filter((clpD) => (
                  filterCompletedBlock(clpD[1]).length > 0)
                )
              }
              return (
                <Page size="A4" style={styles.tablePage} key={index}>
                  {index === 0 ? (
                    <>
                      <View style={styles.header}>
                        <Text>{getTextByCurrentLanguage(property.name)}</Text>
                      </View>

                      <View style={styles.disclaimerRoot}>
                        <View style={styles.infoIcon}>
                          <Text style={{color: 'rgba(11, 35, 92, 1)', fontSize: '13px'}}>i</Text>
                        </View>
                        <Text
                          style={{
                            fontWeight: 600,
                            fontSize: "9px",
                            fontStyle: "Manrope",
                          }}
                        >
                          {t("CLP_pdfDisclaimer")}
                        </Text>
                      </View>
                    </>
                  ): null}
                  {filteredClpDataMap?.slice(item.from, item.to).map((data: any, i: number) => {
                    return (
                      <UnitContainer
                        key={data[0]}
                        data={data}
                        property={getTextByCurrentLanguage(property.name)}
                      />
                    );
                  })}
                </Page>
              )
            })}
        </Document>
      );
    }
  }

  const simulateClick = (e: any) => {
      if (e) e.click()
      setIsPrepareDocument(false)
  }

  const getButtonForDownloadLink = (loading: boolean) => {
    if (loading) {
      return <CircularProgress color="secondary" />
    }

    if (isPrepareDocument) {
      return (
        <IconButton ref={simulateClick}/>
      )
    } else {
      return (
        <IconButton
          color="inherit"
          aria-label="Open drawer"
          onClick={async (e) => {
            e.preventDefault()
            await setNewDocument(DOC())
            await setIsPrepareDocument(true)
          }}
        >
          <Download />
        </IconButton>
      )
    }
  }

  return (
    <div className={classes.downloadBox}>
      <PDFDownloadLink
        document={newDocument}
        fileName={`checklist-${
          company?.name?.toLocaleLowerCase().split(" ").join("-")
        }-${
          getTextByCurrentLanguage(property?.name)?.toLocaleLowerCase().split(" ").join("-")
        }.${new Date().toDateString()}.pdf`}
      >
        {({loading }) => getButtonForDownloadLink(loading)}
      </PDFDownloadLink>
    </div>
  )
}

export default DownloadDocument
